import { useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

const INACTIVITY_TIMEOUT = 120 * 60 * 1000; // 120 minutes in milliseconds
const WARNING_TIME = 5 * 60 * 1000; // Show warning 5 minutes before logout

export const useAutoLogout = () => {
  const navigate = useNavigate();
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const warningTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimers = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current);
      warningTimeoutRef.current = null;
    }
  };

  const handleLogout = async () => {
    clearTimers();
    toast.error("You've been logged out due to inactivity");
    await supabase.auth.signOut();
    navigate("/");
  };

  const showWarning = () => {
    toast.warning("You'll be logged out in 5 minutes due to inactivity", {
      duration: 300000, // Show for 5 minutes
    });
  };

  const resetTimer = () => {
    clearTimers();

    // Set warning timeout (3 hours - 5 minutes)
    warningTimeoutRef.current = setTimeout(() => {
      showWarning();
    }, INACTIVITY_TIMEOUT - WARNING_TIME);

    // Set logout timeout (3 hours)
    timeoutRef.current = setTimeout(() => {
      handleLogout();
    }, INACTIVITY_TIMEOUT);
  };

  useEffect(() => {
    // Check if user is logged in
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!session) return;

      // Start the timer
      resetTimer();

      // Activity events to track
      const events = ["mousedown", "mousemove", "keypress", "scroll", "touchstart", "click"];

      // Reset timer on any user activity
      const handleActivity = () => {
        resetTimer();
      };

      // Add event listeners
      events.forEach((event) => {
        document.addEventListener(event, handleActivity);
      });

      // Cleanup
      return () => {
        clearTimers();
        events.forEach((event) => {
          document.removeEventListener(event, handleActivity);
        });
      };
    });
  }, []);

  return null;
};
